import { useEffect, useMemo, useState } from 'react'
import { getDaysSinceColorClass } from 'utils/formatting'
import { BsCalendar3, BsChevronDown } from 'react-icons/bs'
import { format, differenceInDays } from 'date-fns'
import {
  Handshake,
  MailOpen,
  NotepadText,
  HandCoins,
  Layers,
  LayoutList,
  FileText,
} from 'lucide-react'
import { PiCalendarStar } from 'react-icons/pi'
import { CompanyPageAccordion } from '../CompanyPageAccordion'
import { ResponsiveTabs, ResponsiveTabConfig } from '../../Tabs'
import { EventHistoryList } from './EventHistoryList'
import { Popover, PopoverContent, PopoverTrigger } from '../../UI/popover'
import { Calendar } from '../../UI/calendar'
import { SampleRequestPreviewModal } from '../../Samples/SampleRequestPreviewModal'
import { EventHistoryFilterModal } from './EventHistoryFilterModal'
import { EventHistoryFilterChips } from './EventHistoryFilterChips'
import { EmailsModal } from '../Modals/EmailsModal/EmailsModal'
import { ActivityV2 } from 'models/activityV2'
import { EmailMessage } from 'models/emailIntegration'
import { useEventHistoryFilterStore } from './EventHistoryFilterStore'
import { useEventHistoryQuery } from './useEventHistoryQuery'
import { buildEmailFromEvent } from './utils'
import { StageColorProvider } from 'components/SalesStage/StageColorContext'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'

export type EventHistoryTab =
  | 'all'
  | 'deals'
  | 'notes'
  | 'reminders'
  | 'samples'
  | 'emails'
  | 'documents'
  | 'account_attributes'

// Wrapper to convert react-icons to lucide-style component
const CalendarStarIcon = ({
  size,
  className,
}: {
  size?: number
  className?: string
}) => <PiCalendarStar size={size} className={className} />

const EVENT_HISTORY_TABS: ResponsiveTabConfig<EventHistoryTab>[] = [
  { key: 'all', title: 'All', icon: LayoutList },
  { key: 'deals', title: 'Deals', icon: Handshake },
  { key: 'notes', title: 'Notes', icon: NotepadText },
  { key: 'reminders', title: 'Reminders', icon: CalendarStarIcon },
  { key: 'samples', title: 'Samples', icon: HandCoins },
  { key: 'emails', title: 'Emails', icon: MailOpen },
  { key: 'documents', title: 'Documents', icon: FileText },
  { key: 'account_attributes', title: 'Account Attributes', icon: Layers },
]

interface EventHistoryAccordionProps {
  chainProxyId: number
  doorProxyId?: number
  companyType?: string
}

export function EventHistoryAccordion({
  chainProxyId,
  doorProxyId,
}: EventHistoryAccordionProps) {
  const { dealDetailPage } = useFeatureFlagService()
  const [selectedTab, setSelectedTab] = useState<EventHistoryTab>('all')
  const [dateRange, setDateRange] = useState<
    | {
        from: Date
        to?: Date
      }
    | undefined
  >(undefined)

  // Reset shared filter store on mount to prevent cross-page filter leakage
  useEffect(() => {
    useEventHistoryFilterStore.getState().clearAllFilters()
  }, [chainProxyId])

  // Shared query hook — replaces inline useInfiniteQuery + buildQueryParams
  const {
    groupedEvents,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
  } = useEventHistoryQuery({
    chainProxyId,
    doorProxyId,
    selectedTab,
    dateRange,
  })

  // Sample preview modal state
  const [showPreviewModal, setShowPreviewModal] = useState(false)
  const [previewingSampleId, setPreviewingSampleId] = useState<number | null>(
    null
  )

  const handlePreviewRequest = (sampleId: number) => {
    setPreviewingSampleId(sampleId)
    setShowPreviewModal(true)
  }

  const handleClosePreviewModal = () => {
    setShowPreviewModal(false)
    setPreviewingSampleId(null)
  }

  // Email view modal state
  const [selectedEmail, setSelectedEmail] = useState<EmailMessage | null>(null)

  const handleEmailClick = (event: ActivityV2) => {
    setSelectedEmail(buildEmailFromEvent(event))
  }

  // Header: most recent event + days badge
  const mostRecentEvent = useMemo(() => {
    if (groupedEvents.length === 0) return null
    return groupedEvents[0]?.events?.[0] ?? null
  }, [groupedEvents])

  const daysSinceUpdate = useMemo(() => {
    if (!mostRecentEvent?.created_at) return 0
    return differenceInDays(new Date(), new Date(mostRecentEvent.created_at))
  }, [mostRecentEvent])

  const headerRight = mostRecentEvent ? (
    <>
      <div className="flex items-center gap-1">
        <BsCalendar3 className="w-4 h-4 text-fg-secondary" />
        <span
          className="text-xxs font-light text-fg"
          style={{ lineHeight: '18px' }}
        >
          Updated {format(new Date(mostRecentEvent.created_at), 'MM/dd/yyyy')}
        </span>
      </div>

      <div className="flex h-5 items-center gap-1 rounded-full border border-line bg-surface-raised px-1.5 py-0.5">
        <div
          className={`h-2 w-2 rounded-full ${getDaysSinceColorClass(daysSinceUpdate)}`}
        />
        <span
          className="text-xxs font-light text-fg"
          style={{ lineHeight: '100%' }}
        >
          <span className="font-semibold">{daysSinceUpdate}</span>{' '}
          {daysSinceUpdate === 1 ? 'day' : 'days'}
        </span>
      </div>
    </>
  ) : null

  return (
    <StageColorProvider value={dealDetailPage ? 'pipeline' : 'win_rate'}>
      <CompanyPageAccordion
        title="Event History"
        defaultOpen={true}
        stickyHeader={true}
        headerRight={headerRight}
        noPaddingBottom={true}
      >
        {/* Tabs and Search - static header */}
        <div className="bg-gray-50 space-y-4 pb-4">
          {/* Tabs */}
          <ResponsiveTabs
            tabs={EVENT_HISTORY_TABS}
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
            className="border-b border-gray-200"
          />

          {/* Filters Row */}
          <div className="w-full flex gap-2">
            {/* Date Range Filter */}
            <Popover>
              <PopoverTrigger asChild>
                <button className="flex-1 flex items-center gap-2 px-3.5 py-2 bg-white border border-line rounded-md text-sm text-fg hover:border-fb-dark-green transition-colors">
                  <BsCalendar3 className="w-5 h-5 flex-shrink-0" />
                  <span className="flex-1 text-left">
                    {dateRange?.from ? (
                      dateRange.to ? (
                        <>
                          {format(dateRange.from, 'd MMM yyyy')} -{' '}
                          {format(dateRange.to, 'd MMM yyyy')}
                        </>
                      ) : (
                        format(dateRange.from, 'd MMM yyyy')
                      )
                    ) : (
                      'All time'
                    )}
                  </span>
                  <BsChevronDown className="w-5 h-5 flex-shrink-0" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  initialFocus
                  mode="range"
                  defaultMonth={dateRange?.from}
                  selected={dateRange}
                  onSelect={(newRange) => {
                    if (!newRange?.from) {
                      return
                    }
                    setDateRange(newRange as { from: Date; to?: Date })
                  }}
                  numberOfMonths={2}
                />
                {dateRange && (
                  <div className="p-3 border-t">
                    <button
                      onClick={() => setDateRange(undefined)}
                      className="w-full px-3 py-1.5 text-sm text-gray-600 hover:text-fb-dark-green transition-colors"
                    >
                      Clear dates
                    </button>
                  </div>
                )}
              </PopoverContent>
            </Popover>

            {/* More Filters Button */}
            <EventHistoryFilterModal selectedTab={selectedTab} />
          </div>

          {/* Filter Chips */}
          <EventHistoryFilterChips selectedTab={selectedTab} />
        </div>

        <EventHistoryList
          isLoading={isLoading}
          error={error}
          groupedEvents={groupedEvents}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          fetchNextPage={fetchNextPage}
          onPreviewRequest={handlePreviewRequest}
          onEmailClick={handleEmailClick}
        />

        {/* Sample Preview Modal */}
        {showPreviewModal && (
          <SampleRequestPreviewModal
            show={showPreviewModal}
            onHide={handleClosePreviewModal}
            sampleRequestId={previewingSampleId}
          />
        )}

        {/* Email View Modal */}
        {selectedEmail && (
          <EmailsModal
            show={!!selectedEmail}
            onHide={() => setSelectedEmail(null)}
            emailOnlyMode
            initialEmail={selectedEmail}
          />
        )}
      </CompanyPageAccordion>
    </StageColorProvider>
  )
}
